'use client';
import cn from 'classnames';
import { displayTierName } from '@/util/product_client_safe';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import {
  filterVipFromInsuranceCatalog,
  formatInsuranceOptionLabel
} from '@/util/vip-insurance';
import Image from 'next/image';
import { Tooltip } from 'react-tooltip';

import './SignUpSummaryModal.style.scss';
import ChevronDown from 'public/img/icon/chevron-down-black.svg';
import Close from 'public/img/icon/close.svg';
import CircleQuestionMark from 'public/img/icon/circle-questionmark.svg';

import RadioGroup from '@/components/common/RadioGroup/RadioGroup';
import SignUpSummaryEditMobile from '@/components/common/SignUpSummaryEditMobile/SignUpSummaryEditMobile';
import SignUpPlanCardList from '@/components/common/SignUpPlanCardList/SignUpPlanCardList';
import Text from '@/components/common/Text/Text';

import { SignupState as RegistrationValues } from '@/types/Signup';

import BaseModal from '@/components/modal/lib/BaseModal';
import { useModal } from '@/components/hook/modal';
import { RawEPCCCartResponse } from '@/types/Cart';
import {
  AddOnType,
  InsuranceProduct,
  InsuranceType,
  SubscriptionProduct
} from '@/types/Subscription';
import { capitalize } from '@/util/misc';
import { isBrandAmbassadorCode } from '@/util/form_validation_errors';
import { useAmbassadorRedirectFlag } from '@/components/hook/flags';
import {
  createPreviewSubscriptionCart,
  PreviewCartRequest
} from '@/util/user_client';
import LoadingSpinner from '@/components/common/LoadingSpinner/LoadingSpinner';
import { useSignup } from '@/components/hook/signup';
import { CLOSET_SLOT_TIER_SLUG, CLOSET_SLOT_TIER_STRING } from '@/types/Closet';

type SignUpSummaryModalProps = {
  className?: string;
  subs: SubscriptionProduct[];
  insuranceData: InsuranceProduct[];
  onClose: () => void;
  user: RegistrationValues;
  previewCart?: RawEPCCCartResponse;
};

export type ChangeValueAction =
  | { type: 'addon'; value: AddOnType }
  | { type: 'insurance'; value: InsuranceType }
  | { type: 'referral_code'; value: string }
  | { type: 'subscription_plan'; value: SubscriptionProduct };

function SignUpSummaryModal(props: SignUpSummaryModalProps) {
  const {
    className = '',
    subs,
    insuranceData,
    onClose,
    user,
    previewCart: initialPreviewCart
  } = props;
  const modalTools = useModal();
  const { actions: signupActions } = useSignup();
  const ambassadorRedirectModalEnabled = useAmbassadorRedirectFlag();
  const selectableInsurance = useMemo(
    () => filterVipFromInsuranceCatalog(insuranceData),
    [insuranceData]
  );

  const [editMobileSummary, setEditMobileSummary] = useState<boolean>(false);
  const [referralValue, setReferralValue] = useState(user.referralCode || '');
  const [previewCart, setPreviewCart] = useState<
    RawEPCCCartResponse | undefined
  >(initialPreviewCart);
  const [addOnValue, setAddOnValue] = useState<AddOnType>(
    user.addon_subscription as AddOnType
  );
  const [insuranceLevelValue, setInsuranceLevelValue] = useState<InsuranceType>(
    () => {
      const currentLevel = user.insurance_level as InsuranceType;
      if (selectableInsurance.some((plan) => plan.slug === currentLevel)) {
        return currentLevel;
      }

      return (selectableInsurance[0]?.slug as InsuranceType) ?? 'standard';
    }
  );
  const [selectedPlanValue, setSelectedPlanValue] =
    useState<SubscriptionProduct>(user.plan as SubscriptionProduct);

  const [isGeneratingPreviewCart, setGeneratingPreviewCart] =
    useState<boolean>(false);

  const handleToggleEdit = () => {
    setEditMobileSummary(!editMobileSummary);
  };

  const isAmbassadorCode =
    ambassadorRedirectModalEnabled && isBrandAmbassadorCode(referralValue);

  const referralCopy =
    referralValue.trim() && !isAmbassadorCode
      ? previewCart?.included?.promotions &&
        previewCart?.included?.promotions.length > 0
        ? previewCart?.included?.promotions[0].name
        : 'Applied after acceptance'
      : '';

  useEffect(() => {
    /**
     * Since modals can't inherit updated properties from parent components,
     * use this to regenerate preview cart upon update of membership values
     */
    const regeneratePreviewCart = async () => {
      setPreviewCart(undefined);
      setGeneratingPreviewCart(true);
      const subscriptionProductIds = [selectedPlanValue.productId];
      const promotionCodes: string[] = [];
      let insuranceProductId;

      if (insuranceLevelValue) {
        insuranceProductId = selectableInsurance.find(
          (ins) => ins.slug === insuranceLevelValue
        )?.id;
      }

      if (addOnValue.toLowerCase() === CLOSET_SLOT_TIER_SLUG.PREMIER) {
        const premierProductId = subs.find(
          (sub) => sub.slug.toLowerCase() === CLOSET_SLOT_TIER_SLUG.PREMIER
        )?.productId;
        if (premierProductId) {
          subscriptionProductIds.push(premierProductId);
        }
      }

      // Ambassador codes never apply in this flow — don't preview their discount.
      if (referralValue && !isAmbassadorCode) {
        promotionCodes.push(referralValue);
      }

      const { cart: newPreviewCart } = await createPreviewSubscriptionCart({
        insuranceProductId,
        promotionCodes,
        subscriptionProductIds
      });

      setPreviewCart(newPreviewCart);
      setGeneratingPreviewCart(false);
    };

    regeneratePreviewCart();
  }, [
    addOnValue,
    isAmbassadorCode,
    selectableInsurance,
    insuranceLevelValue,
    referralValue,
    selectedPlanValue.productId,
    subs
  ]);

  const handleChangeValue = (changeValue: ChangeValueAction) => {
    const { type, value } = changeValue;
    switch (type) {
      case 'addon': {
        setAddOnValue(value);
        signupActions.updateFormValues({
          addon_subscription: value
        });
        return;
      }

      case 'subscription_plan': {
        setSelectedPlanValue(value);
        signupActions.updateFormValues({
          plan: value
        });
        return;
      }

      case 'referral_code': {
        const referralCode = value.toUpperCase();
        setReferralValue(referralCode);
        signupActions.updateFormValues({
          referralCode
        });
        return;
      }

      case 'insurance': {
        setInsuranceLevelValue(value);
        signupActions.updateFormValues({
          insurance_level: value
        });
        return;
      }
    }
  };

  const isPremierPlanSelected =
    selectedPlanValue.name === CLOSET_SLOT_TIER_STRING.PREMIER;

  return (
    <BaseModal
      className={cn('SignUpSummaryModal', className)}
      onClose={() => {
        signupActions.updateFormValues({
          addon_subscription: addOnValue,
          insurance_level: insuranceLevelValue,
          referralCode: referralValue
        });

        onClose();
      }}
    >
      <div className='SignUpSummaryModal-mobile'>
        <div className='SignUpSummaryModal-mobile-header'>
          <Text variant='title-xxs-700-upper'>Membership summary</Text>

          {editMobileSummary ? (
            <button
              className='SignUpSummaryBar-edit-button cursor'
              onClick={handleToggleEdit}
            >
              Save
            </button>
          ) : (
            <button
              className='SignUpSummaryModal-mobile-close'
              onClick={onClose}
            >
              <Image
                src={ChevronDown}
                height={16}
                width={16}
                alt=''
              />
            </button>
          )}
        </div>

        {editMobileSummary ? (
          <SignUpSummaryEditMobile
            onValueChange={handleChangeValue}
            addOnValue={addOnValue}
            insuranceValue={insuranceLevelValue}
            referralValue={referralValue}
            referralCopy={referralCopy}
            selectedPlanValue={selectedPlanValue}
            subs={subs}
            previewCart={previewCart}
          />
        ) : (
          <div className='Signup-summary'>
            <p>
              <span className='Summary-label'>Tier: </span>
              {addOnValue.toLowerCase() === CLOSET_SLOT_TIER_SLUG.PREMIER ? (
                <>
                  {displayTierName(selectedPlanValue.name)},{' '}
                  {capitalize(addOnValue)} add-on
                </>
              ) : (
                displayTierName(selectedPlanValue.name)
              )}
            </p>

            <p>
              <span className='Summary-label'>Coverage: </span>
              {capitalize(insuranceLevelValue)}
            </p>

            <p>
              <span className='Summary-label'>Monthly: </span>
              {previewCart?.data?.meta.display_price.without_tax.formatted}
            </p>

            <p>
              <span className='Summary-label'>Referral: </span>
              {referralValue ? referralValue : 'None'}
            </p>
            {!!referralCopy && (
              <p className='Summary-description'>{referralCopy}</p>
            )}

            <div>
              <div className='PlanChoiceBillingCycle-value-due-now'>
                <span className='PlanChoiceBilling-label'>Due Now: </span>
                <span className='PlanChoiceBilling-value'>$0</span>
              </div>
              <div className='Tooltip'>
                <button className='SignUpSummaryBar-tooltip-anchor cursor'>
                  <Image
                    src={CircleQuestionMark}
                    alt='Info'
                    width={14}
                    height={14}
                    style={{ marginTop: '-3px', marginLeft: '3px' }}
                  />
                </button>
              </div>
            </div>
            <Tooltip
              anchorSelect='.SignUpSummaryBar-tooltip-anchor'
              className='SignUpSummaryBar-tooltip cursor'
              classNameArrow='SignUpSummaryBar-tooltip-arrow'
              place='right'
            >
              <div className='SignUpSummaryBar-tooltip-content'>
                <Image
                  className='tooltip-close'
                  src={Close}
                  alt=''
                />
                <Text
                  variant='title-xxs-500'
                  uppercase
                >
                  When will I be charged?
                </Text>
                <Text variant={'text-s'}>
                  Your billing cycle will begin once your membership application
                  has been reviewed by our team and accepted.
                </Text>
              </div>
            </Tooltip>

            <div className='Summary-button-wp'>
              <button
                className='SignUpSummaryBar-edit-button cursor'
                onClick={() => {
                  handleToggleEdit();
                }}
              >
                Edit
              </button>
            </div>
          </div>
        )}
      </div>

      <div className='SignUpSummaryModal-desktop'>
        <SignUpPlanCardList
          subs={subs}
          selectedPlanValue={selectedPlanValue}
          isPremierAddOnSelected={
            addOnValue.toLowerCase() === CLOSET_SLOT_TIER_SLUG.PREMIER
          }
          onCloseModal={() => {
            signupActions.updateFormValues({
              addon_subscription: addOnValue,
              insurance_level: insuranceLevelValue
            });
            modalTools.closeModal();
          }}
          onPlanSelected={(selectedPlanValue: SubscriptionProduct) => {
            handleChangeValue({
              type: 'subscription_plan',
              value: selectedPlanValue as SubscriptionProduct
            });
          }}
        />

        <div className='PlanChoices-bottom-wrapper'>
          <div className='PlanChoiceAddons PlanChoiceOptions'>
            <div className='PlanChoiceBottom-title'>Add-ons:</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
              <span className='PlanChoice-field-info'>
                Add Premier to your membership to experience an additional piece
                from our Premier closet.
              </span>
              <button
                className='SignUpSummaryModal-premier-info-anchor cursor'
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer'
                }}
              >
                <Image
                  src={CircleQuestionMark}
                  alt='Info'
                  width={14}
                  height={14}
                />
              </button>
            </div>

            <Tooltip
              anchorSelect='.SignUpSummaryModal-premier-info-anchor'
              className='SignUpSummaryBar-tooltip cursor'
              classNameArrow='SignUpSummaryBar-tooltip-arrow'
              opacity={1}
              place='top'
            >
              <div className='SignUpSummaryBar-tooltip-content'>
                <Text variant='title-xxs-500-upper'>
                  What&apos;s included in the premier closet?
                </Text>
                <Text variant='text-s'>
                  The Premier closet includes jewelry up to $1,000 and select
                  designer bags.
                </Text>
              </div>
            </Tooltip>

            <RadioGroup
              value={addOnValue}
              onChange={(event: ChangeEvent<HTMLInputElement>): void => {
                const { value } = event.currentTarget;
                handleChangeValue({
                  type: 'addon',
                  value: value as AddOnType
                });
              }}
              name={`${CLOSET_SLOT_TIER_SLUG.PREMIER}-value`}
              options={[
                {
                  label: `${CLOSET_SLOT_TIER_STRING.PREMIER} ${
                    (
                      subs.find(
                        (sub) =>
                          sub.slug.toLowerCase() ===
                          CLOSET_SLOT_TIER_SLUG.PREMIER
                      ) ??
                      subs.sort(
                        (a, b) => a.price.asNumber - b.price.asNumber
                      )[0]
                    ).price.asFormattedString.split('.')[0]
                  }/month`,
                  value: CLOSET_SLOT_TIER_SLUG.PREMIER,
                  disabled: isPremierPlanSelected
                },
                {
                  label: 'None',
                  value: 'none'
                }
              ]}
            />
          </div>
          <div className='PlanChoiceCovarage PlanChoiceOptions'>
            <div className='PlanChoiceBottom-title'>Coverage:</div>
            <span className='PlanChoice-field-info'>
              Included coverage protects against normal wear & tear. Upgrade to
              premium coverage for accidental damage protection.
            </span>
            <RadioGroup
              value={insuranceLevelValue}
              onChange={(event: ChangeEvent<HTMLInputElement>): void => {
                const { value } = event.currentTarget;

                handleChangeValue({
                  type: 'insurance',
                  value: value as InsuranceType
                });
              }}
              name='radiogroup-coverage'
              options={selectableInsurance.map((insurance) => ({
                label: formatInsuranceOptionLabel(insurance),
                value: insurance.slug as InsuranceType
              }))}
            />
          </div>
          <div className='PlanChoiceReferral PlanChoiceOptions'>
            <div className='PlanChoiceBottom-title'>Referral code/name</div>
            {!!referralCopy.trim() && (
              <p className='Summary-description'>{referralCopy}</p>
            )}

            <div className='PlanChoiceReferral-Code'>
              <input
                type='text'
                className={cn('SignUpSummaryEditMobile-referral-code', {
                  success:
                    referralCopy && referralCopy !== 'Applied after acceptance'
                })}
                name='referralCode'
                placeholder='None'
                value={referralValue}
                onChange={(event: ChangeEvent<HTMLInputElement>): void => {
                  const { value } = event.currentTarget;
                  const referralCode = value.toUpperCase();
                  setReferralValue(referralCode ?? '');
                  handleChangeValue({
                    type: 'referral_code',
                    value: referralCode ?? ''
                  });
                }}
              />

              {referralCopy && referralCopy !== 'Applied after acceptance' && (
                <Text
                  className='PlanChoiceReferral-code-succes'
                  variant='text-s'
                >
                  Success! {referralValue} applied!
                </Text>
              )}
            </div>
          </div>
          <div className='PlanChoiceBillingCycle PlanChoiceOptions'>
            <div className='PlanChoiceBottom-title'>your Billing cycle</div>
            <span className='PlanChoice-field-info'>
              {
                'Your billing cycle does not start until your membership application has been reviewed by our team and accepted.'
              }
            </span>
            <div className='PlanChoiceBillingCycle-value-monthly'>
              <span className='PlanChoiceBilling-label'>Monthly: </span>
              {isGeneratingPreviewCart ? (
                <LoadingSpinner size={10} />
              ) : (
                <>
                  {previewCart?.data?.meta.display_price.without_tax.formatted}
                </>
              )}
            </div>
            <div className='PlanChoiceBillingCycle-value-due-now'>
              <span className='PlanChoiceBilling-label'>Due Now: </span>
              <span className='PlanChoiceBilling-value'>$0</span>
            </div>
            <button className='SignUpSummaryBar-tooltip-anchor cursor'>
              <Image
                src={CircleQuestionMark}
                alt='Info'
                width={14}
                height={14}
                style={{ marginTop: '-3px', marginLeft: '3px' }}
              />
            </button>
            <Tooltip
              anchorSelect='.SignUpSummaryBar-tooltip-anchor'
              className='SignUpSummaryBar-tooltip cursor'
              classNameArrow='SignUpSummaryBar-tooltip-arrow'
              place='right'
            >
              <div className='SignUpSummaryBar-tooltip-content'>
                <Image
                  className='tooltip-close'
                  src={Close}
                  alt=''
                />
                <Text
                  variant='title-xxs-500'
                  uppercase
                >
                  When will I be charged?
                </Text>
                <Text variant={'text-s'}>
                  Your billing cycle will begin once your membership application
                  has been reviewed by our team and accepted.
                </Text>
              </div>
            </Tooltip>
          </div>
        </div>
      </div>
    </BaseModal>
  );
}

export default SignUpSummaryModal;
